/**
 * Members-feature-dir color pairs (DNKADM-0269) — the domain-specific literals from
 * `src/features/members/**` that have no exact match in the shared `AdminTokens`/
 * `SemanticMaps` sets (see `../tokens.ts`). Kept here (not inline) so the feature dir
 * stays literal-free under the DNKADM-0269 ESLint guard (`src/theme/**` owns raw colors).
 *
 * LIGHT values are byte-identical to the pre-0269 literals (the 0266 identity
 * constraint). DARK values follow the same translucent-tint + lifted-ink ramp used by
 * `STATUS_DARK`/`buildAccountBadgePairs` — see `accountBadges.ts` for the pattern.
 */

/** One tint/ink pair. */
interface MembersPair {
  fg: string;
  bg: string;
}

export interface MembersMaps {
  /** Unassigned-coach placeholder avatar (`CoachAssignmentControl`). */
  unassignedAvatar: { bg: string };
  /**
   * Role badge chips with no exact shared-map match (`MembersList` roster).
   * `staff` (DNKADM-0360) is the blue family the avatar ramp already carries, matching
   * the platform-members `STAFF` pair so the same role badges identically on both
   * rosters.
   */
  roleBadges: {
    superAdmin: MembersPair;
    staff: MembersPair;
  };
  /** Guest status chips (`MemberGuestsTab`). */
  guestStatus: {
    active: MembersPair;
    archived: MembersPair;
    blocked: MembersPair;
  };
  /** Guest-avatar tint (`MemberGuestsTab`), close to but distinct from the shared blue AVATARS entry. */
  guestAvatar: MembersPair;
  /** A secondary "danger" pair (block/remove actions) distinct from `theme.tokens.error*`. */
  dangerAlt: MembersPair;
  /** Reduce-lot dialog's danger-tinted avatar background + hover shade. */
  reduceLot: { avatarBg: string; hover: string };
  /** Ledger / order-summary tinted surfaces (`LedgerTab`, `MembershipSummaryBar`). */
  ledger: { summaryBg: string };
  /** Misc neutral surfaces with no exact token match. */
  surfaces: { workspace: string };
  /** Photo-crop dialog canvas backdrop (`MemberPhotoDialog`) — a fixed neutral, same both modes (a photo canvas backdrop is not a themed panel, akin to `blackAlpha`). */
  photoDialogSurface: string;
  /** The billing-summary "Paused" variant used by `MembershipSummaryBar` (distinct dot from the shared `STATUS.paused`). */
  billingPaused: { fg: string; bg: string; dot: string };
}

const LIGHT: MembersMaps = {
  unassignedAvatar: { bg: '#EFEDE8' },
  roleBadges: {
    superAdmin: { fg: '#5A3A8A', bg: '#EAE2F4' },
    staff: { fg: '#33567E', bg: '#E7EEF6' },
  },
  guestStatus: {
    active: { fg: '#1F6B43', bg: '#DCEFE3' },
    archived: { fg: '#5B5470', bg: '#ECEAF3' },
    blocked: { fg: '#A8331F', bg: '#F7DDD6' },
  },
  guestAvatar: { fg: '#3A4A63', bg: '#E8EEF6' },
  dangerAlt: { fg: '#A8331F', bg: '#F7DDD6' },
  reduceLot: { avatarBg: '#F7E9E5', hover: '#A53E27' },
  ledger: { summaryBg: '#EEF6F1' },
  surfaces: { workspace: '#FAF9F7' },
  photoDialogSurface: '#2D2A26',
  billingPaused: { fg: '#8A5A12', bg: '#FBEBD3', dot: '#D98A14' },
};

const DARK: MembersMaps = {
  unassignedAvatar: { bg: '#2A2E29' },
  roleBadges: {
    superAdmin: { fg: '#B3ABCB', bg: 'rgba(90,58,138,0.28)' },
    staff: { fg: '#9FC0E8', bg: 'rgba(51,86,126,0.32)' },
  },
  guestStatus: {
    active: { fg: '#7FD3A3', bg: 'rgba(46,157,94,0.18)' },
    archived: { fg: '#B3ABCB', bg: 'rgba(139,130,168,0.2)' },
    blocked: { fg: '#F0A090', bg: 'rgba(168,51,31,0.24)' },
  },
  guestAvatar: { fg: '#9FC0E8', bg: 'rgba(51,86,126,0.32)' },
  dangerAlt: { fg: '#F0A090', bg: 'rgba(168,51,31,0.24)' },
  reduceLot: { avatarBg: 'rgba(192,73,46,0.22)', hover: '#B75A3E' },
  ledger: { summaryBg: 'rgba(46,125,82,0.12)' },
  surfaces: { workspace: '#1A211C' },
  photoDialogSurface: '#2D2A26',
  billingPaused: { fg: '#E8C27A', bg: 'rgba(216,154,46,0.16)', dot: '#D98A14' },
};

/** Per-mode members-dir color pairs. Light === the pre-0269 literals. */
export function buildMembersMaps(mode: 'light' | 'dark'): MembersMaps {
  return mode === 'dark' ? DARK : LIGHT;
}
